import { ChevronRight, MessageSquare, Send, Sparkles, StopCircle } from "lucide-react";
import { useRef, useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { type ChatMessage, useSseChat } from "./useSseChat";

export type InlineChatScope = "document" | "collection" | "selection" | "official";

interface Props {
  scope: InlineChatScope;
  scopeId?: string;
  scopeLabel?: string;
  /** Optional pre-filled filters passed to /v2/ask. */
  filters?: Record<string, unknown>;
}

/**
 * InlineChat — a compact streaming chat sized for the right-hand AiPanel.
 * Reuses useSseChat but exposes a smaller surface: scope chip, last 10
 * messages, "abrir chat completo" handoff to /chat.
 */
export function InlineChat({ scope, scopeId, scopeLabel, filters }: Props) {
  const [input, setInput] = useState("");
  const { messages, isStreaming, send, cancel, clear } = useSseChat();
  const listRef = useRef<HTMLDivElement | null>(null);
  const visible = messages.slice(-20);

  function submit(): void {
    const q = input.trim();
    if (!q || isStreaming) return;
    setInput("");
    void send({
      question: q,
      scope,
      filters: { ...filters, scope_id: scopeId },
    });
    window.setTimeout(() => listRef.current?.scrollTo({ top: 999_999 }), 50);
  }

  return (
    <div className="flex h-full flex-col" data-testid="inline-chat">
      <header className="flex items-center justify-between gap-1 border-b border-(--border-subtle) px-3 py-2 text-xs">
        <div className="flex items-center gap-1.5">
          <MessageSquare size={12} className="text-(--accent-500)" />
          <span className="font-semibold uppercase tracking-wide text-(--text-tertiary)">Chat</span>
          <Badge variant="muted" className="text-[10px]" data-testid="inline-chat-scope">
            {scopeLabel ?? defaultScopeLabel(scope, scopeId)}
          </Badge>
        </div>
        <div className="flex items-center gap-0.5">
          {messages.length > 0 && (
            <button
              type="button"
              className="text-[11px] text-(--text-tertiary) hover:text-(--text-secondary)"
              onClick={clear}
            >
              limpiar
            </button>
          )}
          <a
            href="/chat"
            className="ml-1 inline-flex items-center text-[11px] text-(--accent-500) hover:text-(--accent-600)"
          >
            full <ChevronRight size={10} />
          </a>
        </div>
      </header>

      <div
        ref={listRef}
        className="flex-1 overflow-y-auto px-3 py-2"
        data-testid="inline-chat-messages"
      >
        {visible.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center text-xs text-(--text-tertiary)">
            <Sparkles size={14} className="mb-1 text-(--accent-500)" />
            <p>Pregunta algo sobre {(scopeLabel ?? scopeId ?? scope).toLowerCase()}.</p>
          </div>
        )}
        <ul className="space-y-2">
          {visible.map((m) => (
            <MessageRow key={m.id} message={m} />
          ))}
        </ul>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="border-t border-(--border-subtle) p-2"
      >
        <div className="flex items-center gap-1">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Pregunta sobre ${scopeLabel ?? scope}…`}
            className="h-8 text-xs"
            data-testid="inline-chat-input"
          />
          {isStreaming ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={cancel}
              aria-label="Cancelar"
            >
              <StopCircle size={13} />
            </Button>
          ) : (
            <Button type="submit" variant="primary" size="sm" disabled={!input.trim()}>
              <Send size={13} />
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

function MessageRow({ message }: { message: ChatMessage }) {
  return (
    <li className={message.role === "user" ? "ml-4 text-right" : "mr-4"}>
      <div
        className={
          message.role === "user"
            ? "inline-block max-w-full rounded-md bg-(--accent-50) px-2 py-1 text-xs text-(--text-primary)"
            : "inline-block max-w-full rounded-md bg-(--bg-elevated) px-2 py-1 text-xs"
        }
      >
        <p className="whitespace-pre-wrap break-words">{message.content}</p>
        {message.streaming && (
          <span className="ml-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-(--accent-400)" />
        )}
      </div>
      {message.role === "assistant" && message.sources && message.sources.length > 0 && (
        <div className="mr-4 mt-1 flex flex-wrap gap-1">
          {message.sources.slice(0, 4).map((s) => (
            <a
              key={s.chunk_id}
              href={`/docs/${s.doc_id}${s.section_id ? `#${s.section_id}` : ""}`}
              className="rounded-full bg-(--bg-base) px-1.5 py-0.5 text-[10px] text-(--text-secondary) hover:bg-(--bg-hover)"
            >
              📘 {s.path.split("/").slice(-1)[0]}
            </a>
          ))}
        </div>
      )}
    </li>
  );
}

function defaultScopeLabel(scope: InlineChatScope, scopeId?: string): string {
  switch (scope) {
    case "document":
      return scopeId ? `doc:${scopeId.slice(0, 8)}` : "documento";
    case "collection":
      return scopeId ? `col:${scopeId}` : "colección";
    case "selection":
      return "selección";
    case "official":
      return "oficial";
    default:
      return scope;
  }
}
